import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColDef, GridApi, GridReadyEvent, IServerSideGetRowsParams, ReactGrid } from "@codametrix/grid";
import {
  Box,
  Button,
  Input,
  IconButton,
  Typography,
  Badge,
  CircularProgress,
} from "@mui/material";
import filter from "../../../../public/assets/icons/filter.svg";
import cancel from "../../../../public/assets/icons/cancel.svg";
import search from "../../../../public/assets/icons/search.svg";
import deleteIcon from "../../../../public/assets/icons/delete.svg";
import theme from "../../../theme/Theme";
import { CaseFilters } from "../../molecules/CaseFilters";
import { directoryById } from "../../../queries/DirectoryData";
import {
  SearchProfileEntries,
  UpdateCollectionConfig,
  UpdateProfile
} from "../../../queries/profileData";
import { SaveUserWidgetPreferences } from "../../../queries/SaveUserPreference";
import { caseContext } from "../workItemsTable";
import {
  caseSamplesColumns,
  coderDistributionColumns,
  cqaCaseSampleColumns,
  qaColumns,
  queueListColumns,
  sortColumnDefs,
  normalizeColDefForAGGrid33,
  updatedCaseSamplesColumnDefs,
  updatedCollectionColumnDefs,
  updatedDistributionColumnDefs,
  updatedQAColumnDefs,
} from "../../../utils/columnMapper";
import {
  SearchWorkerCollectionEntries,
  workerCollectionData,
} from "../../../queries/WorkerCollectionData";
import {
  WidgetConstants,
  columnWidth,
  getResourceActions,
  policiesContext,
  serviceLineMapper,
  reverseServiceLineMapper,
} from "../../../utils/common";
import { GetWorkers } from "../../../queries/Workers";
import { DeleteCollection } from "../../molecules/DeleteCollection";
import { SearchQARuleEntries, UpdateQaruleCollectionConfig } from "../../../queries/QAruleData";
import "./index.css";
import { QA_RULE_LEVELS } from "../QARuleForm/constants";
import {SnackbarContext} from "../../../context/AlertsContext";
import { utilGetGroups, utilGetQueues, fetchRubrics, fetchRubricVersions } from "../../../utils/api";
import type { Rubric, RubricVersion } from "../../../types/rubric";
import { Collection } from "../../../types/sd";

type Props = {
  client: any;
  showForm?: (selected: Object, show: boolean) => void;
  setShowDetails?: React.Dispatch<React.SetStateAction<boolean>>;
  DirectoryType?: string;
  showRowSelection?: boolean;
};

import { QueueFilter } from "./types";

interface CustomColDef extends ColDef {
  entityType?: string;
}

const CollectionSettings = (props: Props) => {
  const PAGE_SIZE = 20;
  const RETRY_COUNT = 3;
  var totalCount = PAGE_SIZE;
  var searchCount = PAGE_SIZE;

  const firstSearchRender = useRef(true);
  const firstUpdateRender = useRef(true);
  const retryCount = useRef(0);
  const firstRender = useRef(true);

  const { policies, roles, coderAssignmentConfig, userContext } = useContext(policiesContext);

    const distributionProps ={
      'serviceLine' : userContext?.serviceLine?.name,
      'healthSystemId' : userContext?.activeContext?.healthSystemTenantId
    }
    const showMaxCases = useMemo(() => {
    const config = coderAssignmentConfig?.find(
      (config) => config?.keyname === "max_cases"
    );
    return config?.values.some(
      (valueObj) =>
        valueObj.value === "true" &&
        valueObj.serviceLines.includes(
          reverseServiceLineMapper[userContext?.serviceLine?.name] ??
            userContext?.serviceLine?.name
        )
    );
  }, [
    coderAssignmentConfig,
    userContext?.serviceLine?.name,
    reverseServiceLineMapper,
  ]);
  const {openSnackbar} = useContext(SnackbarContext);

  //region State
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [nameSearch, setNameSearch] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = React.useState<any>([]);
  const [columnDefs, setColumnDefs] = React.useState<any>([]);
  const [filterApplied, setFilterApplied] = React.useState<boolean>(false);
  const [detailCellRendererParams, setDetailCellRendererParams] = React.useState<any>({});
  const [detailGridOptions, setDetailGridOptions] = React.useState({});
  const [entries, setEntries] = useState({});
  const [numberOfFilters, setNumberOfFilters] = React.useState<number>(0);
  const [countOfEntries, setCountOfEntries] = React.useState<number>(0);
  const [optionsDict, setOptionsDict] = React.useState({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showCount, setShowCount] = useState<boolean>(false);
  const [searchAttributeName, setSearchAttributeName] = useState("");
  const [rootIdentifierColumn, setRootIdentifierColumn] = useState<String>("");
  const [rowData, setRowData] = useState([]);
  const [profileIds, setProfileIds] = useState([]);
  const [selected, setSelected] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [availableRecordsCount, setAvailableRecordsCount] = useState(0);

  // Rubric lookup maps for displaying rubric info in columns
  const [rubricsMap, setRubricsMap] = useState<Map<number, Rubric>>(new Map());
  const [rubricVersionsMap, setRubricVersionsMap] = useState<Map<number, RubricVersion>>(new Map());
  //endregion

  const isQaSamples = ["case_samples", "coder_samples", "verification_samples"].includes(props?.DirectoryType);
  const isCqaSamples = props?.DirectoryType === "cqa_samples";
  const isQueue = props?.DirectoryType === WidgetConstants.COLLECTION;
  const isDistribution = props?.DirectoryType === "distribution";
  const gridsWithRowSelection = ["qa"];
  const shouldShowRowSelection = props?.showRowSelection ?? gridsWithRowSelection.includes(props?.DirectoryType);
  
  //region Helper Functions
  const hasCreatePermission = () => {
    return getResourceActions(policies, roles, WidgetConstants.COLLECTION)?.includes("create");
  }

  const handleFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const saveWidgetPreferences = (config: any) => {
    props?.client
      .mutate({
        mutation: SaveUserWidgetPreferences,
        variables: {
          request: {
            widgetType: props?.DirectoryType,
            config,
            service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
          },
        },
        fetchPolicy: "no-cache",
      })
      .then(async (res) => {
        // getWidgetPreferences();
      })
      .catch((err) => {
        console.error(err);
        openSnackbar("Saving User Widget Preferences Failed", "error");
      });
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const ColumDataCall = (data) => {
    let profileIds = [];
    let columnData = [];
    var Columns = data;
    const dict = entries;
    Columns?.forEach((column) => {
      const cellData = column.data;
      if (cellData) {
        Object.keys(cellData)?.map((key) => {
          dict?.[key]?.add(cellData?.[key]);
        });
        columnData.push({
          ...cellData,
          id: column.id,
          ...(isCqaSamples && cellData?.id ? { rule_id: cellData.id } : {})
        });
        profileIds.push(column.id);
      }
    });
    setEntries(dict);
    setProfileIds(profileIds);
    return columnData;
  };

  const buildSearchFilter = (filtersData) => {
    let obj;
    filtersData?.map((filter: any) => {
      const temp = {
        key: filter?.fieldName,
        operation: filter?.operator,
        value:
          filter?.operator === "like" ? `%${filter?.value}%` : filter?.value,
      };
      if (!filter?.join) {
        obj = { ...temp };
      } else {
        const filters = [{ ...obj }, { ...temp }];
        obj = {
          filters,
          combinator: filter?.join,
        };
      }
    });
    if (filtersData?.length === 1) {
      return {
        filters: [obj],
      };
    } else if (!filtersData?.length) {
      return {
        filters: [],
      };
    }
    return obj;
  };

  const applyFilters = (newFilters) => {
    if (newFilters?.length) {
      const body = buildSearchFilter(newFilters);
      firstSearchRender.current = true;
      setShowCount(false);

      if (isDistribution) {
         gridApi.setGridOption('serverSideDatasource',
          updateServerSideDatasource(body, undefined, true)
        );
      } else {
        searchQueues(body);
      }

      setFilterApplied(true);
      setNumberOfFilters(newFilters?.length);
      saveWidgetPreferences({
        columns: columnDefs,
        filters: newFilters,
      });
    }
  };

  const removeFilters = () => {
    setFilters([]);
    setNumberOfFilters(0);
    setFilterApplied(false);
    handleFilterClose();
    setShowCount(false);
    firstSearchRender.current = true;

    if (isDistribution) {
       gridApi.setGridOption('serverSideDatasource',createServerSideDatasource());
    } else {
      searchQueues({ filters: [] });
    }

    saveWidgetPreferences({
      columns: columnDefs,
      filters: [],
    });
  };

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      let filterObj;
      if (nameSearch.length) {
        if (nameSearch.includes(":")) {
          const splitArray = nameSearch.split(":");
          filterObj = {
            key: columnDefs?.find(
              (def) =>
                def.headerName?.toLowerCase() ===
                splitArray[0]?.trim()?.toLocaleLowerCase()
            )?.field,
            operation: "like",
            value: splitArray[1]?.trim(),
          };
        } else {
          filterObj = {
            key: searchAttributeName,
            operation: "like",
            value: `%${nameSearch}%`,
          };
        }
        const body = {
          filters: [filterObj],
        };
        // firstUpdateRender.current = false;
        firstSearchRender.current = true;
        setShowCount(false);

        if (isDistribution) {
           gridApi.setGridOption('serverSideDatasource',
            updateServerSideDatasource(body, undefined, true)
          );
        } else {
          searchQueues(body);
        }

        setFilterApplied(true);
      } else {
        setShowCount(false);
        setFilterApplied(false);

        if (isDistribution) {
           gridApi.setGridOption('serverSideDatasource',createServerSideDatasource());
        } else {
          searchQueues({ filters: [] });
        }
      }
    }
  };

  const handleCloseSearch = () => {
    setNameSearch("");
    setShowCount(false);
    setFilterApplied(false);
    firstSearchRender.current = true;
    if (isDistribution) {
       gridApi.setGridOption('serverSideDatasource',createServerSideDatasource());
    } else {
      searchQueues({ filters: [] });
    }
  };

  function constValueGetter() {
    return "";
  }

  const checkListedColumn = (columnName, directoryType) => {
    let isListedColumn = false;
    switch (directoryType) {
      case "qa":
        isListedColumn = Object.keys(qaColumns)?.includes(columnName);
        break;
      case WidgetConstants.COLLECTION:
        isListedColumn = Object.keys(queueListColumns)?.includes(columnName);
        break;
      case "distribution":
        isListedColumn = Object.keys(coderDistributionColumns)?.includes(
          columnName
        );
        break;
      case "case_samples":
        isListedColumn = Object.keys(caseSamplesColumns)?.includes(columnName);
        break;
      case "coder_samples":
        isListedColumn = Object.keys(caseSamplesColumns)?.includes(columnName);
        break;
      case "verification_samples":
        isListedColumn = Object.keys(caseSamplesColumns)?.includes(columnName);
        break;
      case "cqa_samples":
        isListedColumn = Object.keys(cqaCaseSampleColumns)?.includes(columnName);
        break;
    }
    return isListedColumn;
  };

  const recursiveBuildSchema = (
    parentModel: any,
    relationshipDict: any,
    modelDict: any,
    mappingName?: any
  ) => {
    const obj: any = {};
    const columnFields = [];
    parentModel?.attributes?.map((attribute) => {
      if (
        !attribute?.systemOnly &&
        checkListedColumn(attribute?.name, props?.DirectoryType)
      ) {
        columnFields.push({
          colId: attribute?.name,
          field: attribute.name,
          headerName: attribute.label,
          sortable: true,
          context: {
            id: attribute?.id,
            description: attribute.description,
            entityType: parentModel?.typeName,
          },
          width:
            isQueue
              ? columnWidth(
                  "ag-grid-parent",
                  queueListColumns[attribute?.name]?.width
                )
              : isDistribution
              ? columnWidth(
                  "ag-grid-parent",
                  coderDistributionColumns[attribute?.name]?.width
                )
              : (isQaSamples || isCqaSamples)
              ? columnWidth(
                  "ag-grid-parent",
                  caseSamplesColumns[attribute?.name]?.width || cqaCaseSampleColumns[attribute?.name]?.width
                )
              : columnWidth(
                  "ag-grid-parent",
                  qaColumns[attribute?.name]?.width
                ) ?? 200,
        });
      }
    });

    if (!relationshipDict?.[parentModel?.id]) {
      obj["detailGridOptions"] = {
        columnDefs: columnFields,
        masterDetail: false,
        defaultColDef: {
          flex: 1,
          sortable: true,
          width: 500,
          resizable: true,
        },
      };

      obj["getDetailRowData"] = function (params) {
        return params.successCallback(params?.data?.[mappingName]);
      };

      return obj;
    } else {
      const childValuesOfParent = relationshipDict?.[parentModel?.id];
      var detailParams;
      childValuesOfParent?.map((childValue) => {
        detailParams = recursiveBuildSchema(
          modelDict?.[childValue?.childId],
          relationshipDict,
          modelDict,
          childValue?.mappingName
        );
        if (Object.keys(queueListColumns)?.includes(childValue?.mappingName)) {
          columnFields.push({
            field: childValue?.mappingName,
            headerName: childValue?.mappingName,
            sortable: true,
            width: 200,
            cellRenderer: "agGroupCellRenderer",
            valueGetter: constValueGetter,
          });
        }
      });
      const getRowData = parentModel?.root
        ? undefined
        : function (params) {
            return params.successCallback(params?.data?.[mappingName]);
          };
      obj["detailGridOptions"] = {
        columnDefs: columnFields,
        masterDetail: true,
        detailRowAutoHeight: true,
        detailCellRendererParams: detailParams,
        defaultColDef: {
          flex: 1,
          sortable: true,
          width: 500,
          resizable: true,
        },
      };
      obj["getDetailRowData"] = getRowData;
      return obj;
    }
  };

  const onGridReady = async (params: GridReadyEvent) => {
    setGridApi(params.api);
    let datasource;
    setFilters([]);
    setNumberOfFilters(0);
    setFilterApplied(false);
    setColumnDefs([]);
    firstRender.current = true;
    setShowCount(false);

    if (["distribution", "qa"]?.includes(props?.DirectoryType)) {
      datasource = createServerSideDatasource();
      params.api?.setGridOption('serverSideDatasource', datasource);
    } else {
      searchQueues({ filters: [] });
    }
  };

  function updateServerSideDatasource(
    filterBody: any,
    afterLogin?: boolean,
    sendRowCount?: boolean
  ) {
    return {
      getRows: async function (params: IServerSideGetRowsParams) {
        var order;
        var fieldName;
        if (props?.DirectoryType === "qa") {
          fieldName = "first_name";
          order = "ASC";
        }
        if (params?.request?.sortModel?.length) {
          order = params?.request?.sortModel?.[0]?.sort?.toUpperCase();
          fieldName = params?.request?.sortModel?.[0]?.colId;
        }
        if (afterLogin && firstUpdateRender.current) {
          let variables: any;
          variables = {
            DirectoryType:
              props?.DirectoryType === "qa" ? "worker" : props?.DirectoryType,
          };
          await props?.client
            .query({
              query: directoryById,
              variables: variables,
              fetchPolicy: "no-cache",
            })
            .then((response: any) => {
              const searchKey = response?.data?.DirectoryByType?.searchKey;
              if (searchKey) {
                const searchModel =
                  response?.data?.DirectoryByType?.schema?.models?.find(
                    (tempModel) => tempModel?.id === searchKey?.modelId
                  );
                if (searchModel) {
                  const searchAttribute = searchModel?.attributes?.find(
                    (attr) => attr?.id === searchKey?.attributeId
                  );
                  setSearchAttributeName(searchAttribute?.name);
                }
              }
              const relationshipDict = {};
              response?.data?.DirectoryByType?.schema?.relationships?.map(
                (relationship) => {
                  if (relationshipDict[relationship?.srcId]) {
                    relationshipDict[relationship?.srcId]?.push({
                      childId: relationship?.destnId,
                      mappingName: relationship?.name,
                    });
                  } else {
                    relationshipDict[relationship?.srcId] = [
                      {
                        childId: relationship?.destnId,
                        mappingName: relationship?.name,
                      },
                    ];
                  }
                }
              );
              const modelDict = {};
              var rootModel;
              response?.data?.DirectoryByType?.schema?.models?.map((model) => {
                modelDict[model?.id] = model;
                if (model?.root) {
                  rootModel = model;
                }
              });
              const rootIdentifierName = rootModel?.attributes?.find(
                (tempAttribute) => tempAttribute?.id === rootModel?.identifier
              )?.name;
              setRootIdentifierColumn(rootIdentifierName);

              const cellParams = recursiveBuildSchema(
                rootModel,
                relationshipDict,
                modelDict
              );

              const filteredColumnDefs =
                props?.DirectoryType === "distribution"
                  ? cellParams?.detailGridOptions?.columnDefs?.filter(
                      (columnDef) => {
                        return ["availability"].includes(columnDef.field);
                      }
                    )
                  : cellParams?.detailGridOptions?.columnDefs?.filter(
                      (columnDef) => {
                        return ["description", "name"].includes(
                          columnDef.field
                        );
                      }
                    );
              const lastNameAttribute = rootModel?.attributes?.find(
                (attribute) => attribute?.name === "last_name"
              );
              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "last_name",
                  id: lastNameAttribute?.id,
                  headerName: "Last Name",
                })
              );

              const firstNameAttribute = rootModel?.attributes?.find(
                (attribute) => attribute?.name === "first_name"
              );

              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "first_name",
                  id: firstNameAttribute?.id,
                  headerName: "First Name",
                })
              );

              setDetailGridOptions({
                ...cellParams?.detailGridOptions,
                columnDefs: filteredColumnDefs.map(normalizeColDefForAGGrid33),
              });
              setDetailCellRendererParams(
                cellParams?.detailGridOptions?.detailCellRendererParams
              );

              const columnNames = buildColumnsDict(
                cellParams?.detailGridOptions
              );
              const dict = {};
              columnNames?.map((columnName) => {
                dict[columnName] = [];
              });
              setOptionsDict(dict);
              firstRender.current = false;
              firstUpdateRender.current = false;
              // renderFlag = false;
            })
            .catch((error: any) => {
              params.fail();
              retryCount.current = retryCount.current + 1;
              if (retryCount.current <= RETRY_COUNT)
                firstUpdateRender.current = false;
              openSnackbar("Failed to Load Directory Metadata", "error");
            });
        }
        if (searchCount >= params.request.startRow) {
          let newPageSize = PAGE_SIZE;
          if (searchCount - params.request.startRow < newPageSize) {
            newPageSize = searchCount - params.request.startRow;
          }
          params.request.endRow = params.request.startRow + newPageSize;
          const pageNumber = params.request.startRow / PAGE_SIZE + 1;
          const filters = [...(filterBody?.filters ?? [])];
          const requestBody = {
            combinator: filterBody?.combinator ?? "AND",
            filters,
            pagination: {
              limit: newPageSize,
              offset: (pageNumber - 1) * PAGE_SIZE,
              sort: fieldName,
              order,
            },
          };
          if( props?.DirectoryType === "distribution"){
            requestBody['service'] =  serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name;
          }
          props?.client
            .mutate({
              mutation:
                props?.DirectoryType === "distribution"
                  ? SearchWorkerCollectionEntries
                  : SearchProfileEntries,
              variables: {
                request: requestBody,
              },  
              fetchPolicy: "no-cache",
            })
            .then((response: any) => {
              searchCount =
                props?.DirectoryType === "distribution"
                  ? response.data.SearchWorkerCollection?.total
                  : response.data.SearchProfile?.total;
              setCountOfEntries(searchCount);
              setShowCount(true);
              const columnData = ColumDataCall(
                props?.DirectoryType === "distribution"
                  ? response.data.SearchWorkerCollection?.data
                  : response.data.SearchProfile?.data
              );
              params.success({
                rowData: columnData,
                rowCount: firstSearchRender.current ? searchCount : undefined,
              });
            })
            .catch((error: any) => {
              params.fail();
              retryCount.current = retryCount.current + 1;
              if (retryCount.current <= RETRY_COUNT)
                params.api.retryServerSideLoads();
              openSnackbar("Failed to Load Entries", "error");
            });
        }
      },
    };
  }

  const buildColumnsDict = (detailGridOptions) => {
    let columnNames = [];
    detailGridOptions?.columnDefs?.map((columnDef) => {
      if (!columnDef?.cellRenderer) {
        columnNames.push(columnDef?.id);
      }
    });
    if (detailGridOptions?.masterDetail) {
      const l = buildColumnsDict(
        detailGridOptions?.detailCellRendererParams?.detailGridOptions
      );
      columnNames = [...columnNames, ...l];
    }
    return columnNames;
  };

  function createServerSideDatasource() {
    return {
      getRows: async function (params: IServerSideGetRowsParams) {
        let variables: any;
        var order;
        var fieldName;
        if (props?.DirectoryType === "qa") {
          fieldName = "first_name";
          order = "ASC";
        }
        if (params?.request?.sortModel?.length) {
          order = params?.request?.sortModel?.[0]?.sort?.toUpperCase();
          fieldName = params?.request?.sortModel?.[0]?.colId;
        }
        if (firstRender?.current) {
          variables = {
            DirectoryType:
              props?.DirectoryType === "qa" ? "worker" : props?.DirectoryType,
          };
          await props?.client
            .query({
              query: directoryById,
              variables: variables,
              fetchPolicy: "no-cache",
            })
            .then((response: any) => {
              const relationshipDict = {};
              const searchKey = response?.data?.DirectoryByType?.searchKey;
              if (searchKey) {
                const searchModel =
                  response?.data?.DirectoryByType?.schema?.models?.find(
                    (tempModel) => tempModel?.id === searchKey?.modelId
                  );
                if (searchModel) {
                  const searchAttribute = searchModel?.attributes?.find(
                    (attr) => attr?.id === searchKey?.attributeId
                  );
                  setSearchAttributeName(searchAttribute?.name);
                }
              }
              response?.data?.DirectoryByType?.schema?.relationships?.map(
                (relationship) => {
                  if (relationshipDict[relationship?.srcId]) {
                    relationshipDict[relationship?.srcId]?.push({
                      childId: relationship?.destnId,
                      mappingName: relationship?.name,
                    });
                  } else {
                    relationshipDict[relationship?.srcId] = [
                      {
                        childId: relationship?.destnId,
                        mappingName: relationship?.name,
                      },
                    ];
                  }
                }
              );
              const modelDict = {};
              var rootModel;
              response?.data?.DirectoryByType?.schema?.models?.map((model) => {
                modelDict[model?.id] = model;
                if (model?.root) {
                  rootModel = model;
                }
              });
              const rootIdentifierName = rootModel?.attributes?.find(
                (tempAttribute) => tempAttribute?.id === rootModel?.identifier
              )?.name;
              setRootIdentifierColumn(rootIdentifierName);

              const cellParams = recursiveBuildSchema(
                rootModel,
                relationshipDict,
                modelDict
              );

              const filteredColumnDefs =
                props?.DirectoryType === "distribution"
                  ? cellParams?.detailGridOptions?.columnDefs?.filter(
                      (columnDef) => {
                        return ["availability"].includes(columnDef.field);
                      }
                    )
                  : cellParams?.detailGridOptions?.columnDefs?.filter(
                      (columnDef) => {
                        return ["description", "name"].includes(
                          columnDef.field
                        );
                      }
                    );
              const lastNameAttribute = rootModel?.attributes?.find(
                (attribute) => attribute?.name === "last_name"
              );

              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "last_name",
                  id: lastNameAttribute?.id,
                  headerName: "Last Name",
                })
              );
              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "worker_group",
                  id: "worker_group",
                  headerName: "Group",
                })
              );
              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "collection",
                  id: "collection",
                  headerName: "Queue",
                })
              );
              const firstNameAttribute = rootModel?.attributes?.find(
                (attribute) => attribute?.name === "first_name"
              );

              filteredColumnDefs.push(
                normalizeColDefForAGGrid33({
                  field: "first_name",
                  id: firstNameAttribute?.id,
                  headerName: "First Name",
                })
              );
              setDetailGridOptions({
                ...cellParams?.detailGridOptions,
                columnDefs: filteredColumnDefs.map(normalizeColDefForAGGrid33),
              });
              setColumnDefs(
                sortColumnDefs(
                  cellParams?.detailGridOptions?.columnDefs,
                  props?.DirectoryType
                )
              );

              setDetailCellRendererParams(
                cellParams?.detailGridOptions?.detailCellRendererParams
              );

              const columnNames = buildColumnsDict(
                cellParams?.detailGridOptions
              );
              const dict = {};
              columnNames?.map((columnName) => {
                dict[columnName] = [];
              });
              setOptionsDict(dict);
              firstRender.current = false;
            })
            .catch((error: any) => {
              params.fail();
              retryCount.current = retryCount.current + 1;
              if (retryCount.current <= RETRY_COUNT)
                firstRender.current = false;
            });
        }
        if (
          !firstRender.current &&
          ["distribution", "qa"]?.includes(props?.DirectoryType)
        ) {
          if (totalCount >= params.request.startRow) {
            let variables= {
              DirectoryType: WidgetConstants.COLLECTION,
              pageSize: PAGE_SIZE,
              pageNumber: params.request.startRow / PAGE_SIZE + 1,
              sort: fieldName,
              order
            }
            if( props?.DirectoryType === "distribution"){
              variables['service'] =  serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name;
            }
            props?.client
              .query({
                query:
                  props?.DirectoryType === "distribution"
                    ? workerCollectionData
                    : GetWorkers,
                variables: variables,
                fetchPolicy: "no-cache",
              })
              .then((response: any) => {
                totalCount =
                  props?.DirectoryType === "distribution"
                    ? response.data.WorkerCollection?.total
                    : response.data.Workers?.total;
                setCountOfEntries(totalCount);
                setShowCount(true);
                const columnData = ColumDataCall(
                  props?.DirectoryType === "distribution"
                    ? response.data.WorkerCollection?.data
                    : response.data.Workers?.data
                );
                params.success({
                  rowData: columnData,
                  rowCount: firstSearchRender.current ? totalCount : undefined,
                });
              })
              .catch((error: any) => {
                console.error(error);
                params.fail();
                retryCount.current = retryCount.current + 1;
                if (retryCount.current <= RETRY_COUNT)
                  params.api.retryServerSideLoads();
              });
          }
        }
      },
    };
  }

  const searchQueues = async (filterBody) => {
    if (firstRender?.current) {
      fetchMetadata();
    }
    setLoading(true);

    // Default filtering checks for Active queues
    let requestBody: QueueFilter = {
      combinator: "AND",
      filters: [{
        key: "status",
        operation: "eq",
        value: "ACTIVE"
      }
    ]
    };

    if(props?.DirectoryType === WidgetConstants.COLLECTION) {
      requestBody = {
        ...requestBody,
        service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
        filters: requestBody.filters.concat(
          {
            key: "service",
            operation: "eq",
            value: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
          }
        ),
      }
    }

    if (isQaSamples || isCqaSamples) {
      requestBody = {
        ...requestBody,
        filters: requestBody.filters.concat(
          [
            {
              key: "level",
              operation: "eq",
              value: QA_RULE_LEVELS[props?.DirectoryType] ?? 0
            },
            {
              key: "service",
              operation: "eq",
              value: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
            }
          ]
        ),
        level: parseInt(QA_RULE_LEVELS[props?.DirectoryType] ?? 0),
        service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
      }
    }

    // If additional filtering exists,
    // nest it beneath the default filter
    if (filterBody?.filters?.length) {
      requestBody.filters.push({
        combinator: filterBody?.combinator ?? "AND",
        filters: filterBody.filters
      })
    };
    const searchVar = props?.DirectoryType === WidgetConstants.COLLECTION ? 'SearchProfile' : 'SearchQarule'
    props?.client
      .mutate({
        mutation: props?.DirectoryType === WidgetConstants.COLLECTION ? SearchProfileEntries : SearchQARuleEntries,
        variables: {
          request: requestBody,
        },
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        totalCount = response.data[searchVar]?.total;
        setCountOfEntries(totalCount);
        setShowCount(true);
        const columnData = ColumDataCall(response.data[searchVar]?.data);
        setRowData(columnData);
        // gridApi.hideOverlay();
      })
      .catch((error: any) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchMetadata = async () => {
    const variables = { DirectoryType: WidgetConstants.COLLECTION };

    await props?.client
      .query({
        query: directoryById,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const relationshipDict = {};
        const searchKey = response?.data?.DirectoryByType?.searchKey;
        if (searchKey) {
          const searchModel =
            response?.data?.DirectoryByType?.schema?.models?.find(
              (tempModel) => tempModel?.id === searchKey?.modelId
            );
          if (searchModel) {
            const searchAttribute = searchModel?.attributes?.find(
              (attr) => attr?.id === searchKey?.attributeId
            );
            setSearchAttributeName(searchAttribute?.name);
          }
        }
        response?.data?.DirectoryByType?.schema?.relationships?.map(
          (relationship) => {
            if (relationshipDict[relationship?.srcId]) {
              relationshipDict[relationship?.srcId]?.push({
                childId: relationship?.destnId,
                mappingName: relationship?.name,
              });
            } else {
              relationshipDict[relationship?.srcId] = [
                {
                  childId: relationship?.destnId,
                  mappingName: relationship?.name,
                },
              ];
            }
          }
        );
        const modelDict = {};
        var rootModel;
        response?.data?.DirectoryByType?.schema?.models?.map((model) => {
          modelDict[model?.id] = model;
          if (model?.root) {
            rootModel = model;
          }
        });
        const rootIdentifierName = rootModel?.attributes?.find(
          (tempAttribute) => tempAttribute?.id === rootModel?.identifier
        )?.name;
        setRootIdentifierColumn(rootIdentifierName);

        const cellParams = recursiveBuildSchema(
          rootModel,
          relationshipDict,
          modelDict
        );

        const filteredColumnDefs =
          cellParams?.detailGridOptions?.columnDefs?.filter((columnDef) => {
            return ["description", "name"].includes(columnDef.field);
          });
        setDetailGridOptions({
          ...cellParams?.detailGridOptions,
          columnDefs: filteredColumnDefs.map(normalizeColDefForAGGrid33),
        });
        setFilterApplied(false);

        setColumnDefs(
          sortColumnDefs(
            cellParams?.detailGridOptions?.columnDefs,
            props?.DirectoryType
          )
        );

        setDetailCellRendererParams(
          cellParams?.detailGridOptions?.detailCellRendererParams
        );

        const columnNames = buildColumnsDict(cellParams?.detailGridOptions);
        const dict = {};
        columnNames?.map((columnName) => {
          dict[columnName] = [];
        });
        setOptionsDict(dict);
        firstRender.current = false;
      })
      .catch((error: any) => {
        retryCount.current = retryCount.current + 1;
        if (retryCount.current <= RETRY_COUNT) firstRender.current = false;
      });
  };

  const getDragDropColumns = () => {
    return [
      normalizeColDefForAGGrid33({
        rowDrag: true,
        field: "dragdrop",
        headerName: "",
        entityType: WidgetConstants.COLLECTION,
        width: columnWidth("ag-grid-parent", 3),
      }),
      normalizeColDefForAGGrid33({
        field: "rank",
        headerName: "Rank",
        entityType: WidgetConstants.COLLECTION,
        width: columnWidth("ag-grid-parent", 6),
      }),
      normalizeColDefForAGGrid33({
        field: "menu",
        headerName: "",
        entityType: WidgetConstants.COLLECTION,
        width: columnWidth("ag-grid-parent", 5),
      }),
      normalizeColDefForAGGrid33({
        field: "delete",
        headerName: "",
        entityType: WidgetConstants.COLLECTION,
        width: columnWidth("ag-grid-parent", 5),
        onCellClicked: (e) => {
          setDeleteModalOpen(true);
          setSelected(e);
        },
        cellRenderer: () => {
          return (
            <IconButton
              disableRipple={true}
              style={{
                width: "50px",
                verticalAlign: "top"
              }}
            >
              <img
                style={{ cursor: "pointer" }}
                src={deleteIcon}
                alt="delete"
              />
            </IconButton>
          );
        }
      })
    ];
  };

  const onRowDragEnd = (event) => {
    const { node, overIndex } = event;
    handleRankSelection(node.rowIndex, overIndex);
  };

  const handleRankSelection = (rowIndex, overIndex) => {
    const newData = [...rowData];
    const config_obj = {};
    newData.splice(
      overIndex === -1 ? rowData?.length : overIndex,
      0,
      newData.splice(rowIndex, 1)[0]
    );
    newData.forEach((item, index) => {
      item.rank = index + 1;
      config_obj[item?.collection_id] = index + 1;
    });

    let variables: any = {
      config: config_obj,
      ...((!isQaSamples || !isCqaSamples) && {service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name})
    }

    if(isQaSamples || isCqaSamples) {
      variables = {
        ...variables,
        level: parseInt(QA_RULE_LEVELS[props?.DirectoryType]),
        service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name
      }
    }

    props?.client
      .mutate({
        mutation: (isQaSamples || isCqaSamples) ? UpdateQaruleCollectionConfig : UpdateCollectionConfig,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      .then(() => {
        setRowData(newData);
      })
      .catch((err) => {
        setRowData([...rowData]);
      });
  };

  const handleDelete = async (e) => {
    const { data, parent } = e?.node;
    const rows = parent?.allLeafChildren?.map(child => child?.data);
    if (data !== undefined) {
      rows.splice(e.rowIndex, 1)
      const profile = {
        status: "INACTIVE"
      }
      props?.client
      .mutate({
        mutation: UpdateProfile,
        variables: {
          updateProfileId: data.collection_id,
          profile: profile,
          forceDisableWithActiveCases: data?.type === "LOCAL_COLLECTION" && data?.remaining_cases > 0
        },
        fetchPolicy: "no-cache",
      })
      .then(() => {
        setRowData(rows);
        setDeleteModalOpen(false);
      })
      .catch((err) => {
        setRowData([...rows]);
      });
    }
  }

  const getTotalCountAsString = () => {
    if (countOfEntries > 1) {
      return `Found ${countOfEntries} records`;
    } else {
      return `Found ${countOfEntries} record`;
    }
  };

  // Filters are a nested model of no more than 2 filters at a time
  // With >2 filters, the first filter in the list is a list of 2 more filters
  // This function is recursive so that it reaches the deepest level first,
  // finding the bottom level which will include a filter object rather than a list.
  // It then climbs back up adding the 2nd value of each list, which is the filter immediately
  // following the previously converted one.

  // Filter: one of { combinator, [Filter, Filter] } | FilterObject
  // FilterObject: { combinator, filter: { key, operation, value} }

  const convertFilter = (filter, combinator) => {
    let filterList: any[] = [];
    if (filter?.filters && filter.combinator) {
      filter.filters.forEach((deeperFilter, i) => {
        const deepestFilter = convertFilter(
          deeperFilter,
          i === 0 ? undefined : filter.combinator
        );
        filterList = filterList.concat(deepestFilter);
      });
    } else {
      const convertedFilter = {
        fieldId: filter.id ?? filter.key,
        fieldName: filter.key,
        operator: filter.operation,
        value: filter.value,
        join: combinator,
      };

      filterList.push(convertedFilter);
    }

    return filterList;
  };

  const convertFilters = (filters) => {
    let newFilters = [];

    // Handle both old array format and new object format
    const rules = Array.isArray(filters) ? filters[0]?.rules : filters?.rules;

    rules?.filters?.forEach((filter, i) => {
      newFilters = newFilters.concat(
        convertFilter(filter, i !== 0 ? rules.combinator : undefined)
      );
    });

    newFilters = newFilters.map((newFilter, i) => {
      return { ...newFilter, filterIndex: i };
    });

    return newFilters;
  };

  const updateColumnDefs = (type, columnDefs) => {
    let updatedColumnDefs;
    switch (type) {
      case "qa":
        updatedColumnDefs = updatedQAColumnDefs(columnDefs, props?.client);
        break;
      case "distribution":
        updatedColumnDefs = updatedDistributionColumnDefs(
          columnDefs,
          props?.client,
          hasCreatePermission(),
          showMaxCases,
          distributionProps
        );
        break;
      case WidgetConstants.COLLECTION:
        updatedColumnDefs = updatedCollectionColumnDefs(
          columnDefs,
          handleRankSelection,
          props?.client,
          availableRecordsCount,
          setAvailableRecordsCount,
        );
        break;
      case "case_samples":
        updatedColumnDefs = updatedCaseSamplesColumnDefs(
          columnDefs,
          handleRankSelection,
          props?.client
        );
        break;
      case "coder_samples":
        updatedColumnDefs = updatedCaseSamplesColumnDefs(
          columnDefs,
          handleRankSelection,
          props?.client
        );
        break;
      case "verification_samples":
        updatedColumnDefs = updatedCaseSamplesColumnDefs(
          columnDefs,
          handleRankSelection,
          props?.client
        );
        break;
      case "cqa_samples":
        updatedColumnDefs = updatedCaseSamplesColumnDefs(
          columnDefs,
          handleRankSelection,
          props?.client,
          "cqa_samples",
          rubricsMap,
          rubricVersionsMap
        );
        break;
      default:
        updatedColumnDefs = columnDefs;
        break;
    }

    return updatedColumnDefs?.map(normalizeColDefForAGGrid33) ?? updatedColumnDefs;
  };

  const getAllGroupsAndQueues = async () => {
      const groups = await utilGetGroups(props?.client);
      const queues = await utilGetQueues(props?.client, userContext?.serviceLine?.name, userContext?.activeContext?.healthSystemTenantId);
      const updatedOptionsDict = JSON.parse(JSON.stringify(optionsDict));
      updatedOptionsDict['collection'] = queues.map(queue => ({ label: queue?.name, value: queue?.value}));
      updatedOptionsDict['worker_group'] = groups.map(group => ({ label: group?.data?.name, value: group?.data?.group_id }));
      setOptionsDict(updatedOptionsDict);
  }

  const updateAvailableCount = () => {
    const count = rowData.filter(row => row.availability === "AVAILABLE" || row.availability === true).length;
    setAvailableRecordsCount(count);
  };

  //endregion

  //region Lifecycle
  useEffect(() => {
    if (props?.DirectoryType === WidgetConstants.COLLECTION || props?.DirectoryType === 'case_samples' || props?.DirectoryType === 'verification_samples' || props?.DirectoryType === "cqa_samples") {
      const dragColumns = ["rank", "dragdrop", "menu", "delete"];
      if (filterApplied) {
        const hasDragColumns = [...columnDefs]?.some((columnDef) =>
          dragColumns.includes(columnDef?.field)
        );
        if (hasDragColumns) {
          setColumnDefs(
            [...columnDefs]?.filter(
              (columnDef) => !dragColumns.includes(columnDef?.field)
            )
          );
        }
      } else if (!columnDefs?.find((def) => def?.field === "dragdrop")) {
        setColumnDefs(
          sortColumnDefs(
            [...columnDefs, ...getDragDropColumns()],
            props?.DirectoryType
          )
        );
      }
    }
    // Coder Samples doesn't have drag and drop functionality but can still delete items
    else if(props?.DirectoryType === "coder_samples") {
      let deleteColumn = getDragDropColumns()[3];
      const hasDeleteColumn = [...columnDefs]?.some((columnDef) =>
        columnDef?.field === "delete"
      );

      if(!hasDeleteColumn)
        setColumnDefs(
          sortColumnDefs(
            [...columnDefs, deleteColumn],
            props?.DirectoryType
          )
        );
    }
  }, [columnDefs, filterApplied, props?.DirectoryType]);

  useEffect(() => {
    if (isDistribution) {
      getAllGroupsAndQueues();
    }
  }, []);

  // Fetch rubrics for CQA samples
  useEffect(() => {
    if (isCqaSamples) {
      const loadRubricData = async () => {
        try {
          const rubrics = await fetchRubrics();
          const rubricMap = new Map<number, Rubric>();
          rubrics.forEach(r => rubricMap.set(r.id, r));
          setRubricsMap(rubricMap);

          const versionsMap = new Map<number, RubricVersion>();
          await Promise.all(
            rubrics.map(async (rubric) => {
              try {
                const versions = await fetchRubricVersions(rubric.id);
                versions.forEach(v => versionsMap.set(v.id, v));
              } catch (err) {
                console.error(`Error fetching versions for rubric ${rubric.id}:`, err);
              }
            })
          );
          setRubricVersionsMap(versionsMap);
        } catch (err) {
          console.error("Error fetching rubrics:", err);
        }
      };

      loadRubricData();
    }
  }, [isCqaSamples]);

  // Update the count when rowData changes
  useEffect(() => {
    updateAvailableCount();
  }, [rowData]);
  //endregion

  return (
    <>
      <Box>
        <DeleteCollection
          selected={selected}
          open={deleteModalOpen}
          handleClose={() => setDeleteModalOpen(false)}
          handleDelete={handleDelete}
          availableRecordsCount={availableRecordsCount}
        />

        {/*Actions & Search*/}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginRight: "20px",
            backgroundColor: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: "15px",
              width: "100%",
              margin: "20px 0",
            }}
          >
            {/*Inputs*/}
            {props?.DirectoryType !== "qa" && (
              <Box sx={{ alignSelf: "center" }}>
                <Input
                  sx={{ width: "14vw" }}
                  value={nameSearch}
                  placeholder={`Search by ${searchAttributeName}`}
                  onChange={(e: any) => {
                    setNameSearch(e.target.value);
                  }}
                  onKeyPress={handleKeyPress}
                  endAdornment={
                    !!nameSearch?.length && (
                      <IconButton
                        onClick={handleCloseSearch}
                        disableRipple={true}
                      >
                        <img
                          src={cancel}
                          alt="cancel"
                          style={{ height: "14px" }}
                        />
                      </IconButton>
                    )
                  }
                  startAdornment={
                    !nameSearch?.length && (
                      <IconButton disableRipple={true}>
                        <img
                          src={search}
                          alt="search"
                          style={{ height: "14px" }}
                        />
                      </IconButton>
                    )
                  }
                />
              </Box>
            )}

            {(isQueue || isDistribution) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Badge
                  badgeContent={numberOfFilters}
                  color="success"
                  invisible={numberOfFilters === 0}
                >
                  <Button
                    onClick={handleFilter}
                    disableRipple={true}
                    sx={{
                      gap: "5px",
                      paddingY: "8px",
                      backgroundColor:
                        numberOfFilters > 1 ? "#e1e3e1" : "unset",
                      "&:hover": {
                        backgroundColor:
                          numberOfFilters > 1 ? "#cfd1cf" : "auto",
                      },
                    }}
                  >
                    <img
                      src={filter}
                      alt="search"
                      height={"14px"}
                      width={"14px"}
                    />
                    <Typography
                      variant="subtitle1"
                      color="artisticTextMedium.main"
                      fontWeight={400}
                    >
                      Filter
                    </Typography>
                  </Button>
                </Badge>
                <caseContext.Provider
                  value={{
                    directoryType: (isQaSamples || isCqaSamples) ? WidgetConstants.COLLECTION : props?.DirectoryType,
                    columnOptions: columnDefs,
                    detailGridOptions,
                    optionsDict: optionsDict,
                    setOptionsDict: setOptionsDict,
                  }}
                >
                  <CaseFilters
                    client={props?.client}
                    filters={filters}
                    setFilters={setFilters}
                    anchorEl={filterAnchorEl}
                    handleClose={handleFilterClose}
                    applyFilters={applyFilters}
                    removeFilters={removeFilters}
                  />
                </caseContext.Provider>
              </Box>
            )}

            {loading && (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                <CircularProgress color="primary" size={20} />
              </Box>
            )}

            <Box
              style={{
                display: "flex",
                alignSelf: "center",
                flexGrow: 1,
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {showCount && (
                <Typography
                  sx={{
                    display: "flex",
                    justifySelf: "flex-end",
                    alignSelf: "center",
                    color: theme.palette.artisticTextMedium.main,
                  }}
                >
                  {getTotalCountAsString()}
                </Typography>
              )}

              {((props?.DirectoryType === WidgetConstants.COLLECTION && hasCreatePermission()) || (isQaSamples || isCqaSamples))
                  && (
                      <Button
                          variant="contained"
                          onClick={() => props?.showForm({}, true)}
                          sx={{height: "40px", boxShadow: "none"}}
                      >
                        {isQaSamples ? 'Create a sample' : isCqaSamples ? "Create project" : 'Create queue'}
                      </Button>
                  )
              }

              {props?.DirectoryType === "qa" && (
                  <Button
                      variant="contained"
                      onClick={() => props?.showForm({}, true)}
                      sx={{height: "40px", boxShadow: "none"}}
                  >
                    Create QA Rule
                  </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/*Data Grid*/}
        <div
           data-testid="ag-grid-parent"
          id="ag-grid-parent"
          style={{
            height: isQaSamples ? "60vh" : "70vh",
          }}
        >
          {loading && isCqaSamples && (
            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "inherit"}}>
              <CircularProgress size={80}/>
            </Box>
          )}
          <ReactGrid
            keepDetailRows={true}
            rowSelection={shouldShowRowSelection ? { mode: "multiRow",groupSelects: 'descendants' } : undefined}
            detailCellRendererParams={detailCellRendererParams}
            masterDetail={true}
            rowModelType={
              ["distribution", "qa"]?.includes(props?.DirectoryType)
                ? "serverSide"
                : "clientSide"
            }
            rowData={rowData}
            onGridReady={onGridReady}
            columnDefs={updateColumnDefs(props?.DirectoryType, columnDefs)}
            cellSelection={false}
            suppressCellFocus={true}
            detailRowAutoHeight={true}
            {...(["distribution", "qa"]?.includes(props?.DirectoryType) ? { cacheBlockSize: PAGE_SIZE } : {})}
            onRowClicked={(e) => {
              const target = e.event.target as HTMLElement;
              if ((target.localName !== 'img' && !('checked' in target))
                && (isQueue || isQaSamples || isCqaSamples)
                && e.data
              ) {
                props?.showForm({
                  ...e.data,
                  filters: convertFilters(e.data?.filters),
                  id: e.data?.id
                }, true);
              }
            }}
            onRowDragEnd={onRowDragEnd}
          />
        </div>
      </Box>
    </>
  );
};

export default CollectionSettings;
